import type { ProviderGetImage } from '@nuxt/image'
import { createOperationsGenerator } from '#image'
import { joinURL, withoutTrailingSlash } from 'ufo'

const operationsGenerator = createOperationsGenerator({
  keyMap: {
    resize: 'rs',
    size: 's',
    fit: 'rt',
    width: 'w',
    height: 'h',
    dpr: 'dpr',
    enlarge: 'el',
    extend: 'ex',
    gravity: 'g',
    crop: 'c',
    padding: 'pd',
    trim: 't',
    rotate: 'rot',
    quality: 'q',
    maxBytes: 'mb',
    background: 'bg',
    backgroundAlpha: 'bga',
    blur: 'bl',
    sharpen: 'sh',
    watermark: 'wm',
    preset: 'pr',
    cacheBuster: 'cb',
    stripMetadata: 'sm',
    stripColorProfile: 'scp',
    autoRotate: 'ar',
    filename: 'fn',
    format: 'f',
  },
  formatter: (key: string, value: string | number) => `${key}:${value}`,
})

function urlSafeBase64(value: string) {
  return btoa(value)
    .replace(/=+/g, '')
    .replace(/[+/]/g, m => (m === '+' ? '-' : '_'))
}

const defaultModifiers = {
  fit: 'fill',
  width: 0,
  height: 0,
  gravity: 'no',
  enlarge: 1,
  format: 'webp',
}

function isImageSrc(src: string) {
  return /\.(?:jpe?g|png|webp|avif|gif|ico|svg|heic|bmp|tiff?)$/i.test(src)
}

export const getImage: ProviderGetImage = (src, { modifiers, baseUrl }) => {
  const config = useRuntimeConfig()

  const proxyBaseUrl = baseUrl ?? config.public.imgproxyBaseUrl

  if (!proxyBaseUrl)
    return { url: src }

  const isLocalPath = src.startsWith('/')
  const hasImageExt = isImageSrc(src)

  if (isLocalPath && !hasImageExt)
    return { url: src }

  const fullImageUrl = isLocalPath ? `${withoutTrailingSlash(config.public.i18n.baseUrl)}${src}` : src

  const encodedUrl = urlSafeBase64(fullImageUrl)
  const operations = operationsGenerator({ ...defaultModifiers, ...modifiers })
  const path = joinURL('/', operations, encodedUrl)

  return {
    url: joinURL(proxyBaseUrl, 'i', path),
  }
}
